import React, { useCallback, useRef } from 'react'
import { useSelector } from 'react-redux'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import pluralize from 'pluralize'
import { userSelector } from '@src/selectors/app'
import { AllCoachProfilesQuery } from '@src/graphql/fetchAllCoachProfiles.generated'
import {
  EhrAppointmentAttendanceChargeKind,
  EhrAppointmentCollection,
  EhrAppointmentKind,
  EhrAppointmentRecurrenceCadence,
  EhrCoachSpecialty,
  UserInsurancePolicyKind,
} from '@src/types'
import { useDispatchAsync } from '@src/utils/dispatch'
import { useSnack } from '@src/utils/navigatorContext'
import { OnboardingStackParamsList } from '@src/navigation/types'
import Storage from '@src/utils/storage'
import { SeeAllCoaches } from '@src/screens/NutritionistHub/components/SeeAllCoaches/SeeAllCoaches'
import { SpecialtyKey } from './UnifiedOnboardingCoachingTeam/types'

const PER_PAGE = 2

const generateChooseCoachButtonText = (
  coachProfile: AllCoachProfilesQuery['allCoachProfiles']['coachProfiles'][0],
) => {
  return `Book with ${coachProfile.fullName.split(' ')[0]}`
}

const getStoredInsuranceKind = () => {
  const storedInsuranceProvider = Storage.get<string>(Storage.ONBOARDING_INSURANCE_PROVIDER_KEY)

  return (Object.values(UserInsurancePolicyKind) as string[]).includes(
    storedInsuranceProvider as string,
  )
    ? (storedInsuranceProvider as UserInsurancePolicyKind)
    : undefined
}

export const UnifiedOnboardingSelectNutritionist = () => {
  const dispatchAsync = useDispatchAsync()

  const user = useSelector(userSelector)

  const pendingAppointmentsRequest = useRef<Promise<EhrAppointmentCollection> | null>(null)

  const navigation = useNavigation<StackNavigationProp<OnboardingStackParamsList>>()

  const route = useRoute<
    RouteProp<OnboardingStackParamsList, 'UnifiedOnboardingSelectNutritionist'>
  >()
  const { coachingKind, nextScreen, selectedSpecialties = [] } = route.params || {}

  const isStressManagementFlow = coachingKind === 'stress'

  const isFitnessFlow = coachingKind === 'fitness'

  const getCoachTitle = () => {
    if (isStressManagementFlow) {
      return 'Stress Coach'
    }

    return 'Nutritionist'
  }

  const showSnack = useSnack()

  const onNoCoachesFound = useCallback(() => {
    showSnack(`No nutritionists found`, null, 'warning')
  }, [showSnack])

  const loadCoachProfiles = useCallback(
    ({ page, specialties }: { page: number; specialties: EhrCoachSpecialty[] }) => {
      return dispatchAsync<AllCoachProfilesQuery['allCoachProfiles']>({
        type: 'nutritionistHub/fetchAllCoachProfiles',
        payload: {
          recommended: false,
          page,
          licensedState: user?.address?.state,
          perPage: PER_PAGE,
          specialties: isStressManagementFlow ? [] : specialties,
          appointmentKind: isStressManagementFlow
            ? EhrAppointmentKind.StressManagementMnt
            : EhrAppointmentKind.Initial_60,
          insurancePlan: getStoredInsuranceKind(),
          excludedCoachIds: [],
        },
      })
    },
    [dispatchAsync, isStressManagementFlow, user?.address?.state],
  )

  const onCoachSelect = useCallback(
    async (coachProfile: AllCoachProfilesQuery['allCoachProfiles']['coachProfiles'][0]) => {
      const coachingKinds = Storage.get<SpecialtyKey[]>(Storage.ONBOARDING_COACHING_KINDS_KEY)

      const appointmentKind = isStressManagementFlow
        ? EhrAppointmentKind.StressManagementMnt
        : EhrAppointmentKind.Initial_60

      const insuranceKind = getStoredInsuranceKind()

      if (!pendingAppointmentsRequest.current) {
        pendingAppointmentsRequest.current = dispatchAsync<EhrAppointmentCollection>({
          type: 'nutritionistHub/fetchPendingEhrAppointments',
        }).catch((error) => {
          pendingAppointmentsRequest.current = null
          throw error
        })
      }

      const { appointments } = await pendingAppointmentsRequest.current

      const pendingFreeAppointment = appointments.find(
        (appointment) =>
          appointment.attendances[0].chargeKind === EhrAppointmentAttendanceChargeKind.BsFree,
      )
      const finalAppointmentKind = pendingFreeAppointment
        ? pendingFreeAppointment.kind
        : appointmentKind

      navigation.navigate('CallScheduling', {
        appointmentKind: finalAppointmentKind,
        allowBackNavigation:
          !coachingKinds || !isStressManagementFlow || coachingKinds[1] !== 'stress',
        switchingCoach: false,
        showCoachProfile: false,
        insuranceKind,
        cadenceOptions: [
          {
            text: 'One Time',
            value: EhrAppointmentRecurrenceCadence.OneTime,
          },
          {
            text: 'Weekly',
            value: EhrAppointmentRecurrenceCadence.Weekly,
          },
          {
            text: 'Monthly',
            value: EhrAppointmentRecurrenceCadence.Monthly,
          },
        ],
        coachProfile,
        nextScreen,
        pendingAppointmentId: pendingFreeAppointment?.id,
      })
    },
    [isStressManagementFlow, navigation, nextScreen, dispatchAsync],
  )

  const handleShowFilters = useCallback(() => {
    navigation.navigate('CoachSwitchSpecialtiesFilterModal', {
      parentScreen: route.name,
      selectedSpecialties,
    })
  }, [navigation, route.name, selectedSpecialties])

  return (
    <SeeAllCoaches
      navigationTitle={`Choose Your ${getCoachTitle()}`}
      title={`Top Match ${pluralize(getCoachTitle())}`}
      loadMoreButtonText="Show More Matches"
      perPage={PER_PAGE}
      selectedSpecialties={selectedSpecialties}
      loadCoachProfiles={loadCoachProfiles}
      onNoCoachesFound={onNoCoachesFound}
      onCoachSelect={onCoachSelect}
      generateChooseCoachButtonText={generateChooseCoachButtonText}
      showFilters={!isStressManagementFlow}
      alertMessage={
        isFitnessFlow
          ? 'Fitness coaching is provided by athletic-performance nutritionists'
          : undefined
      }
      onShowFilters={!isStressManagementFlow ? handleShowFilters : undefined}
    />
  )
}
